import { action, observable } from 'mobx';

import {
    IReviewJsonData,
    IReviewsApiResult,
    RatingsAndReviewsApiSdk
} from '../../api/RatingsAndReviewsApiSdk';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '../../../src/service-setup/RatingsReviewsService';
import { ReviewSnippet } from '../domain/entities/ReviewSnippet';
import { Review } from '../domain/entities/Review';
import {
    DEFAULT_ANSWERED_QUESTIONS,
    DEFAULT_AVERAGE_RATING,
    DEFAULT_REVIEW_COUNT
} from '../constants/PowerReviews';

const RATINGS_FETCH_DEBOUNCE_TIMEOUT = 300;

interface IProductsRating {
    [key: string]: ReviewSnippet;
}

interface IStashedPromise {
    resolver(productsSnippet: IProductsRating): void;
    rejecter(): void;
    productIds: string[];
}

export class RatingsRepository {
    private apiSdk: RatingsAndReviewsApiSdk;
    private stashedProductIds: string[] = [];
    private ratingsQueryTimeout: number;
    private stashedPromises: IStashedPromise[] = [];

    @observable public productRatings: IProductsRating = {};

    constructor() {
        this.apiSdk = diContainer.get(serviceNames.ratingsReviewsApiSdk);
    }

    public getProductsRating = (productIds: string[]) => {
        return this.debounceGetRatings(productIds);
    };

    public getProductRating = (productId: string) => {
        return this.getProductsRating([productId]);
    };

    public getProductReviews = async (productId: string) => {
        const [reviewsApiResult]: IReviewsApiResult[] = await this.apiSdk.getReviewsData(productId);

        return reviewsApiResult.reviews.map((review: IReviewJsonData) => Review.fromJson(review));
    };

    @action
    private fetchProductsRating = async () => {
        const stashedProductIds = this.stashedProductIds;
        const stashedPromises = this.stashedPromises;
        this.stashedPromises = [];

        if (stashedProductIds.length) {
            this.stashedProductIds = [];

            const data = await this.apiSdk.getRatingSnippetsData(stashedProductIds);
            const productRatings = { ...this.productRatings };

            this.productRatings = stashedProductIds.reduce((accumulator, productId) => {
                const dataItem = data.find(item => item.page_id === productId);

                accumulator[productId] = dataItem
                    ? ReviewSnippet.fromJson(dataItem)
                    : this.getEmptyRating(productId);

                return accumulator;
            }, productRatings);
        }

        this.resolvePromises(stashedPromises);
    };

    private createPromise = (productIds: string[]) => {
        return new Promise((resolver, rejecter) => {
            this.stashedPromises.push({
                resolver,
                rejecter,
                productIds
            });
        });
    };

    private debounceGetRatings = (productIds: string[]) => {
        const newProductIds = this.getNewProductIdsToStash(productIds);

        if (newProductIds.length) {
            clearTimeout(this.ratingsQueryTimeout);

            this.stashedProductIds = [...new Set([...this.stashedProductIds, ...newProductIds])];
            this.ratingsQueryTimeout = setTimeout(
                this.fetchProductsRating,
                RATINGS_FETCH_DEBOUNCE_TIMEOUT
            );

            return this.createPromise(productIds);
        }

        return this.getProductsRatingsById(productIds);
    };

    private resolvePromises = (stashedPromises: IStashedPromise[]) => {
        stashedPromises.forEach(this.resolvePromise);
    };

    private resolvePromise = (promise: IStashedPromise) => {
        const productReviews = this.getProductsRatingsById(promise.productIds);

        promise.resolver(productReviews);
    };

    private getProductsRatingsById(productIds: string[]) {
        return productIds.reduce((ratingsList: IProductsRating, productId) => {
            const productRating = this.productRatings[productId];
            const _ratingsList = { ...ratingsList };

            _ratingsList[productId] = productRating || this.getEmptyRating(productId);

            return _ratingsList;
        }, {});
    }

    private getNewProductIdsToStash(productIds: string[]) {
        return productIds.reduce((accumulator: string[], productId) => {
            const newProductIds = [...accumulator];

            if (!this.productRatings[productId]) {
                newProductIds.push(productId);
            }

            return newProductIds;
        }, []);
    }

    private getEmptyRating(pageId: string): ReviewSnippet {
        return ReviewSnippet.fromJson({
            page_id: pageId,
            rollup: {
                average_rating: DEFAULT_AVERAGE_RATING,
                review_count: DEFAULT_REVIEW_COUNT,
                answered_questions: DEFAULT_ANSWERED_QUESTIONS
            }
        });
    }
}
